import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import axios from 'axios'
import { useCallback, useMemo, useState } from 'react'
import { SubmitErrorHandler, UseFormHandleSubmit } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useImportedInvoiceDrawer } from '@features/importedInvoices/context/importedInvoiceDrawer'
import { useApproveImportedInvoice } from '@features/importedInvoices/hooks/useApproveImportedInvoice'
import { ImportedInvoiceReviewFormValues } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'
import { getImportedInvoiceReviewApprovableSchema } from '@features/importedInvoices/utils/getImportedInvoiceReviewApprovableSchema'
import { parseImportedInvoiceReviewApprovable } from '@features/importedInvoices/utils/parseImportedInvoiceReviewApprovable'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { getApproveImportedInvoicePayload } from '../utils/getApproveImportedInvoicePayload'

const BAD_REQUEST_STATUS = 400
const UNPROCESSABLE_STATUS = 422

interface UseImportedInvoiceReviewApproveOptions {
  handleSubmit: UseFormHandleSubmit<ImportedInvoiceReviewFormValues>
  isSubmitting: boolean
  onInvalidSubmit?: SubmitErrorHandler<ImportedInvoiceReviewFormValues>
}

/**
 * Validates the form and approves it. `isApproving` covers both, so the form can't be edited or
 * approved twice while either runs. What a failure shows depends on what went wrong:
 * - 400: a message in the form. The schema already checks every field approve requires;
 * - 422: a message in the form. The row is no longer approvable or the client is unknown, and
 *   nothing was written. The invoice is refetched, so a row approved elsewhere opens its detail view;
 * - anything else (404, 502): a toast. The row is not shown as approved.
 */
export const useImportedInvoiceReviewApprove = ({
  handleSubmit,
  isSubmitting,
  onInvalidSubmit,
}: UseImportedInvoiceReviewApproveOptions) => {
  const [approveErrorMessage, setApproveErrorMessage] = useState<string | null>(null)
  const { t } = useTranslation()
  const { importedInvoiceId } = useImportedInvoiceDrawer()
  const { taxRatesIncludingDeactivated } = useTaxRates()
  const approvableSchema = useMemo(() => getImportedInvoiceReviewApprovableSchema(t), [t])

  const notifyApproveFailure = useCallback(() => {
    notify({
      id: NotificationKeys.ImportedInvoiceApprove,
      message: t('imported_invoices.review.approve.error.generic'),
      variant: 'error',
    })
  }, [t])

  const handleApproveError = useCallback(
    (error: unknown) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined

      if (status === BAD_REQUEST_STATUS) {
        setApproveErrorMessage(t('imported_invoices.review.approve.error.invalid'))

        return
      }

      if (status === UNPROCESSABLE_STATUS) {
        setApproveErrorMessage(t('imported_invoices.review.approve.error.unprocessable'))

        return
      }

      notifyApproveFailure()
    },
    [notifyApproveFailure, t],
  )

  const handleApproveSuccess = useCallback(() => {
    notify({
      id: NotificationKeys.ImportedInvoiceApprove,
      message: t('imported_invoices.review.approve.success'),
      variant: 'success',
    })
  }, [t])

  const { mutateAsync: approveAsync } = useApproveImportedInvoice({
    onError: handleApproveError,
    onSuccess: handleApproveSuccess,
  })

  const handleValidSubmit = useCallback(
    async (values: ImportedInvoiceReviewFormValues) => {
      setApproveErrorMessage(null)

      const { result } = parseImportedInvoiceReviewApprovable(approvableSchema, values)

      if (!importedInvoiceId || !result.success) {
        trackError('Imported invoice review passed validation without the values approve requires')
        notifyApproveFailure()

        return
      }

      const payload = getApproveImportedInvoicePayload(result.data, taxRatesIncludingDeactivated ?? [])

      if (!payload) {
        trackError('Imported invoice review has a VAT rate that is not one of the organization sales tax rates')
        notifyApproveFailure()

        return
      }

      try {
        await approveAsync({ importedInvoiceId, payload })
      } catch {
        // Already handled by the mutation's `onError`; awaiting only keeps `isSubmitting` true until here.
      }
    },
    [approvableSchema, approveAsync, importedInvoiceId, notifyApproveFailure, taxRatesIncludingDeactivated],
  )

  const approveReview = useCallback(() => {
    handleSubmit(handleValidSubmit, onInvalidSubmit)()
  }, [handleSubmit, handleValidSubmit, onInvalidSubmit])

  return { approveErrorMessage, approveReview, isApproving: isSubmitting }
}
